<template>
  <form class="message-form" @submit="onSubmit">
    <!-- STAFF PORTAL SETTINGS -->
    <div v-if="loaded && staffView">
      <!-- Message scheduler -->
      <div class="message-scheduler-wrapper">
        <!-- Scheduler dropdown (Send now or Scheduled) -->
        <md-field v-if="user.isStaff">
          <label for="messageScheduler">Schedule Message</label>
          <md-select
            v-model="messageSchedule"
            name="messageSchedule"
            id="messageSchedule"
            :disabled="saving"
          >
            <md-option
              v-for="type in Message.options.MESSAGE_SCHEDULE_CHOICES"
              :value="type.value"
              :key="type.value"
              :selected="type.default"
            >
              {{ type.label }}
            </md-option>
          </md-select>
        </md-field>

        <!-- Scheduled message date picker -->
        <md-field :class="{ 'md-has-value': this.messageSendDatetime }">
          <datetime
            v-if="user.isStaff && messageSchedule === Message.options.SCHEDULED"
            v-model="messageSendDatetime"
            type="datetime"
            input-class="md-input"
            input-id="messageSendDatetime"
            :minute-step=10
            :min-datetime="nowToISOString()"
          >
            <font-awesome-icon
            class="calendar"
            :icon="['fa', 'calendar']"
            :style="{ fontSize: '24px' }"
            slot="before"
            ></font-awesome-icon>
            <label for="messageSendDatetime" slot="before">Scheduled Datetime</label>
          </datetime>
        </md-field>
      </div>

      <!-- Message Type -->
      <md-field v-if="user.isStaff">
        <label for="messageType">Select a Type</label>
        <md-select
          v-model="message.messageType"
          name="messageType"
          id="messageType"
          :disabled="saving"
          @md-selected="onChangeMessageType"
        >
          <md-option
            v-for="type in Message.options.MESSAGE_TYPE_CHOICES"
            :value="type.value"
            :key="type.value"
          >
            {{ type.label }}
          </md-option>
        </md-select>
      </md-field>

      <!-- Template -->
      <md-field
        v-if="
          [
            Message.options.DIGITAL_MESSAGE_ENGLISH,
            Message.options.DIGITAL_MESSAGE_SPANISH,
          ].includes(message.messageType)
        "
      >
        <label for="template">Select a Template</label>
        <md-select
          v-model="selectedTemplate"
          name="template"
          id="template"
          :disabled="saving"
          @md-selected="onChangeTemplate"
        >
          <md-option
            v-for="template in this.messageTemplates"
            :value="template.id"
            :key="template.name"
          >
            {{ template.name }}
          </md-option>
        </md-select>
      </md-field>

      <!-- Category -->
      <md-field>
        <label for="category">Select a Category</label>
        <md-select v-model="message.category" :disabled="saving">
          <md-option
            v-for="category in Message.options.MESSAGE_CATEGORY_CHOICES"
            :value="category.value"
            :key="category.value"
          >
            {{ category.label }}
          </md-option>
        </md-select>
      </md-field>
    </div>

    <md-field
      v-if="
        ![
          Message.options.DIGITAL_MESSAGE_ENGLISH,
          Message.options.DIGITAL_MESSAGE_SPANISH,
        ].includes(message.messageType)
      "
    >
      <label>Notes</label>
      <md-textarea v-model="message.note" :disabled="saving" class="big-box"></md-textarea>
    </md-field>

    <div
      v-if="
        [Message.options.DIGITAL_MESSAGE_ENGLISH, Message.options.DIGITAL_MESSAGE_SPANISH].includes(
          message.messageType,
        )
      "
      class="form-field"
    >
      <div v-if="staffView" class="md-field md-theme-default md-has-textarea">
        <textarea
          v-if="staffView"
          v-model="message.text"
          :disabled="saving"
          class="md-textarea big-box"
          :placeholder="placeholderText"
        ></textarea>
      </div>
      <textarea
        v-else
        v-model="message.text"
        :disabled="saving"
        class="form-field-textarea"
        :placeholder="placeholderText"
        @input="mixin_TextAreaResize_resize"
      ></textarea>
    </div>

    <div class="form-footer">
      <div class="button-container">
        <button type="submit" class="form-button" :class="{ 'dark': darkText && !saving }" :disabled="saving">
          <i v-translate v-if="!saving">Send</i>
          <LoadSpinner v-if="saving" :color="`#${organizationDetails.darkBrandColor}`" />
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { translate } from 'vue-gettext'
import { LoadSpinner } from '@/components'
import { mapState } from 'vuex'
import Message from '@/services/messages'
import MessageTemplates from '@/services/messageTemplates'
import { Datetime } from 'vue-datetime'
import '@/services/utils'
import { formatNumberAsUSD } from '@/services/utils'
import { formatText, formatValue } from '../../services/utils'
import TextAreaResize from '@/mixins/TextAreaResize'

export default {
  name: 'MessageForm',
  components: { LoadSpinner, datetime: Datetime },
  mixins: [TextAreaResize],
  props: {
    request: {
      type: Object,
      default: () => {},
    },
    stipendRequest: {
      type: Object,
      default: () => {},
    },
    staffView: {
      type: Boolean,
      default: false,
    },
    msgUser: {
      type: Object,
      default: {},
        default: () => {},
    },
    darkText: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      Message,
      message: Message.create({
        type: this.$props.requestType,
        request: this.$props.request?.id,
        stipendRequest: this.$props.stipendRequest?.id,
        messageType: !this.staffView
          ? Message.options.DIGITAL_MESSAGE_ENGLISH || Message.options.DIGITAL_MESSAGE_SPANISH
          : null,
        scheduledDatetime: null,
      }),
      saving: false,
      loaded: false,
      selectedTemplate: null,
      messageTemplates: null,
      selectedTargetLanguage: null,
      messageSchedule: Message.options.SEND_NOW,
      messageSendDatetime: null,
    }
  },
  methods: {
    onSubmit(e) {
      e.preventDefault()
      // If sending a message from the staff portal, require a category be selected.
      if (this.staffView && !this.message.category) {
        this.$Alert.alert({
          type: 'error',
          message: `<p>Please select a message category.</p>`,
          timeout: 3000,
        })
        return
      }
      if (
        [Message.options.DIGITAL_MESSAGE_ENGLISH, Message.options.DIGITAL_MESSAGE_SPANISH].includes(
          this.message.messageType,
        ) &&
        !this.message.text
      ) {
        this.$Alert.alert({
          type: 'error',
          message: `<p>Please make sure your message contains text.</p>`,
          timeout: 3000,
        })
        return
      }
      this.saving = true

      // Determine if we should send now, or if this is a scheduled message
      if(this.messageSchedule === Message.options.SEND_NOW) {
        // Send now
        this.message.status = Message.options.MESSAGE_SENT
        Message.api
        .create(this.message)
        .then(message => {
          this.message = Message.create({
            request: this.$props.request?.id,
            stipendRequest: this.$props.stipendRequest?.id,
            messageType: !this.staffView
              ? Message.options.DIGITAL_MESSAGE_ENGLISH || Message.options.DIGITAL_MESSAGE_SPANISH
              : null,
            sendDatetime: null,
            status: Message.options.MESSAGE_SENT,
          })
          this.$emit('created', message)
          this.selectedTemplate = null
        })
        .finally(() => {
          this.saving = false
        })
      } else {
        // Scheduled message

        // If we don't have a datetime to send, do nothing
        if(!this.messageSendDatetime) {
          this.saving = false
          return
        }

        // Set the schedule datetime on the message & schedule it for delivery
        this.message.sendDatetime = this.messageSendDatetime
        this.message.status = Message.options.MESSAGE_SCHEDULED
        Message.api
        .create(this.message)
        .then(message => {
          this.message = Message.create({
            request: this.$props.request?.id,
            stipendRequest: this.$props.stipendRequest?.id,
            messageType: !this.staffView
              ? Message.options.DIGITAL_MESSAGE_ENGLISH || Message.options.DIGITAL_MESSAGE_SPANISH
              : null,
            sendDatetime: this.messageSendDatetime,
            status: Message.options.MESSAGE_SCHEDULED,
          })
          this.$emit('created', message)
          this.selectedTemplate = null
        })
        .finally(() => {
          this.saving = false
        })
      }
      
    },
    // Admin-only
    async onChangeMessageType() {
      this.setTargetLanguage()
      await this.fetchTemplates()
      this.onChangeTemplate()
    },
    // Admin-only
    onChangeTemplate() {
      const templateInfo = this.messageTemplates.find(
        message_template => {
            return message_template.id == this.selectedTemplate
          }
      )
      let currentLang = 'en'
      // let templateInfoText = templateInfo.text
      if (this.message.messageType == Message.options.DIGITAL_MESSAGE_SPANISH) {
        // templateInfoText = templateInfo.textEs
        currentLang = 'es'
      }
      this.message.text = formatValue(
        templateInfo?.text || '',
        {
          org: this.$props.request.organizationRef || this.$props.msgUser.organizationRef,
          user: this.$props.msgUser,
          admin: this.$store.state.user,
          request: this.$props.request,
          stipendRequest: this.$props.stipendRequest,
        },
      )

      this.message.category = templateInfo?.category
    },
    // Admin-only
    setTargetLanguage() {
      const isSpanish = [Message.options.DIGITAL_MESSAGE_SPANISH].includes(this.message.messageType)

      this.selectedTargetLanguage = isSpanish ? 'es-ES' : 'en-US'
    },
    // Admin-only
    async fetchTemplates() {
      const lang = this.selectedTargetLanguage
      const response = await MessageTemplates.api.list(lang)
      this.messageTemplates = response.results
    },
    nowToISOString() {
      let now = new Date()
      return now.toISOString()
    },
  },
  async created() {
    /*this.updateMessageType(this.user)*/
    if (this.staffView) {
      const response = await MessageTemplates.api.list()
      this.messageTemplates = response.results
    }
    this.loaded = true
  },
  updated() {
    this.loaded = true
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    placeholderText() {
      return this.$gettext('Type a new message')
    },
  },
}
</script>

<style lang="scss" scoped>
.message-form {
  display: flex;
  flex-direction: column;

  .form-field {
    margin-bottom: 0;
  }

  .message-scheduler-wrapper {
    display: flex;
    flex-direction: row;
    align-items: top;
  }

  .datepicker {
    width: 20rem;
    padding-top: 0;
    margin-top: 0;
    .md-field { margin-bottom: 0; }
  }

  .md-field .md-select {
    max-width: 20rem;
  }

  .md-textarea {
    padding-top: 0 !important;
    min-height: 18rem !important;
    font-size: 14px !important;
  }
}
.vdatetime {
  display: flex;

  svg {
    margin-right: 1rem;
  }

  label {
    margin-left: 36px;
  }
}
</style>
